import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { LogIn, LogOut, Smartphone } from 'lucide-react';

export const ManualPunchCard = ({ onPunched }) => {
  const [st, setSt] = useState(null);
  const [branch, setBranch] = useState('');
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());

  const load = useCallback(async () => {
    try {
      const d = (await axios.get(`${API}/biometric/mobile-punch/status`)).data;
      setSt(d);
      setBranch(b => b || d.default_branch || '');
    } catch { setSt(null); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);

  if (!st) return null;
  const action = st.clocked_in ? 'out' : 'in';

  const punch = async () => {
    if (!branch) { toast.error('Please select a branch'); return; }
    setBusy(true);
    try {
      const d = (await axios.post(`${API}/biometric/mobile-punch`, { action, branch })).data;
      setSt(s => ({ ...s, ...d }));
      toast.success(d.message);
      onPunched?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Punch failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 shadow-sm" data-testid="manual-punch-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${st.clocked_in ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'}`}>
            <Smartphone className="w-5 h-5" />
          </div>
          <div>
            <div className="text-sm font-semibold text-stone-800" data-testid="manual-punch-status">
              {st.clocked_in ? 'You are clocked in' : 'You are clocked out'}
            </div>
            <div className="text-xs text-stone-500">
              {now.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Johannesburg' })} ·{' '}
              {st.punches.length ? `Today: ${st.punches.map(p => p.time).join(' · ')}` : 'No punches yet today'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Select value={branch} onValueChange={setBranch} disabled={st.clocked_in}>
            <SelectTrigger className="w-full sm:w-44" data-testid="manual-punch-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
            <SelectContent>{st.branches.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={punch} disabled={busy || !branch} data-testid="manual-punch-btn"
            className={`min-w-[130px] text-white ${st.clocked_in ? 'bg-rose-700 hover:bg-rose-800' : 'bg-emerald-700 hover:bg-emerald-800'}`}>
            {st.clocked_in ? <LogOut className="w-4 h-4 mr-1" /> : <LogIn className="w-4 h-4 mr-1" />}
            {busy ? 'Saving…' : st.clocked_in ? 'Clock Out' : 'Clock In'}
          </Button>
        </div>
      </div>
    </div>
  );
};
